import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { api } from '../../api.ts';
import { Avatar } from '../../components/Avatar.tsx';
import { BottomNav } from '../../components/BottomNav.tsx';
import { Thread } from '../../components/Thread.tsx';
import { TopBar } from '../../components/TopBar.tsx';
import { relativeTime, type ConversationSummary } from '../../messages.ts';

type Area = 'extra' | 'resto';

/** Liste des conversations de l'extra ou de l'établissement, avec accès au support et réglage des e-mails. */
export function MessagesPage({ area }: { area: Area }) {
  const navigate = useNavigate();
  const [items, setItems] = useState<ConversationSummary[] | null>(null);
  const [emails, setEmails] = useState(true);
  const [busy, setBusy] = useState(false);

  async function load() {
    const res = await api.get<{ conversations: ConversationSummary[]; preferences: { emailNotifications: boolean } }>('/messages/conversations');
    setItems(res.conversations);
    setEmails(res.preferences.emailNotifications);
  }

  useEffect(() => {
    void load();
    const timer = setInterval(() => document.visibilityState === 'visible' && void load(), 15_000);
    return () => clearInterval(timer);
  }, []);

  async function openSupport() {
    setBusy(true);
    try {
      const res = await api.post<{ id: string }>('/messages/conversations', { support: true });
      navigate(`/${area}/messages/${res.id}`);
    } finally {
      setBusy(false);
    }
  }

  async function toggleEmails(value: boolean) {
    setEmails(value);
    await api.put('/messages/preferences', { emailNotifications: value }).catch(() => setEmails(!value));
  }

  return (
    <div className="app-shell">
      <TopBar title="Messages" />
      <div className="screen-content">
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          {area === 'extra'
            ? 'Posez vos questions aux établissements et suivez vos échanges. Tout se passe ici : aucun numéro ni e-mail ne circule.'
            : 'Échangez avec les extras qui postulent ou que vous avez retenus. Tout se passe ici : aucun numéro ni e-mail ne circule.'}
        </div>

        {items === null && <div className="empty-state">Chargement…</div>}
        {items && items.length === 0 && <div className="empty-state">Aucune conversation pour l’instant.{area === 'extra' ? ' Ouvrez une mission pour écrire à l’établissement.' : ' Écrivez à un candidat depuis une mission.'}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items?.map((c) => (
            <Link key={c.id} to={`/${area}/messages/${c.id}`} className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
              {c.kind === 'MISSION' && area === 'resto' ? (
                <Avatar avatar={c.avatar} size={44} label={c.counterpart} />
              ) : (
                <span aria-hidden style={{ width: 44, height: 44, borderRadius: '50%', background: c.kind === 'SUPPORT' ? 'var(--success-tint)' : 'var(--accent-tint)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: c.kind === 'SUPPORT' ? 'var(--success)' : 'var(--accent-dark)', flexShrink: 0 }}>
                  {c.kind === 'SUPPORT' ? '?' : c.counterpart.charAt(0).toUpperCase()}
                </span>
              )}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ fontWeight: c.unread ? 800 : 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.counterpart}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', flexShrink: 0 }}>{relativeTime(c.lastMessageAt)}</div>
                </div>
                {c.mission && <div style={{ fontSize: 12, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.mission.title}</div>}
                <div style={{ fontSize: 13, color: c.unread ? 'var(--text)' : 'var(--text-secondary)', fontWeight: c.unread ? 700 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {c.lastMessage?.urgent && <Zap size={12} style={{ color: 'var(--danger)', marginRight: 4 }} aria-label="Urgent" />}
                  {c.lastMessage ? c.lastMessage.body : 'Nouvelle conversation'}
                </div>
              </div>
              {c.unread > 0 && <span className="badge" style={{ background: 'var(--accent)', color: '#fff' }} aria-label={`${c.unread} non lu${c.unread > 1 ? 's' : ''}`}>{c.unread}</span>}
            </Link>
          ))}
        </div>

        <button className="btn btn-secondary" disabled={busy} onClick={openSupport}>Écrire au support Mon Extra</button>

        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, lineHeight: 1.5 }}>
          <input type="checkbox" checked={emails} onChange={(e) => toggleEmails(e.target.checked)} style={{ marginTop: 3 }} />
          <span>Me prévenir par e-mail quand un message reste sans réponse. L’e-mail ne contient pas le message, seulement un lien vers l’application.</span>
        </label>
      </div>
      <BottomNav />
    </div>
  );
}

export function ConversationPage({ area }: { area: Area }) {
  const { id } = useParams();
  return <Thread conversationId={id!} missionPath={(missionId) => `/${area}/missions/${missionId}`} />;
}
